import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import PageTitle from "../../PageTitle";
import LeaveNavigation from "./navigation/LeaveNavigation";
import useAxios from "../../../utilities/useAxios";
import { getCurrentUserProfile } from "../../../utilities/AuthService";
import {
	Form,
	Input,
	DatePicker,
	Select,
	Button,
	message,
	Card,
	ConfigProvider,
	Alert,
	Layout,
} from "antd";
import dayjs from "dayjs";
import "dayjs/locale/ko";
import locale from "antd/lib/locale/ko_KR";
import useIsMobile from "../../../hooks/useIsMobile";
import PageHeader from "../../common/PageHeader";

dayjs.locale("ko");

const { Option } = Select;
const { TextArea } = Input;
const { Content } = Layout;

const ApplyForSpecialLeave = () => {
	const isMobile = useIsMobile();
	const location = useLocation();
	const isSick = location.pathname.includes("apply-sick");
	const leaveType = isSick ? "병가" : "경조사";
	const title = isSick ? "병가 신청" : "경조휴가 신청";

	const [form] = Form.useForm();
	const [loading, setLoading] = useState(false);
	const [userProfile, setUserProfile] = useState(null);

	useEffect(() => {
		message.config({ top: 150 });
		return () => message.config({ top: 8 });
	}, []);

	useEffect(() => {
		form.resetFields(["reason", "start_date", "end_date"]);
		(async () => {
			try {
				const profile = await getCurrentUserProfile();
				setUserProfile(profile);
				form.setFieldsValue({
					name: profile.name,
					position:
						profile.classification_name ===
						"교역자"
							? "교역자"
							: "교직원",
					application_date: dayjs(),
				});
			} catch (e) {
				message.error(
					"사용자 정보를 불러오는데 실패했습니다.",
				);
			}
		})();
	}, [form, leaveType]);

	const onFinish = async (values) => {
		setLoading(true);
		try {
			const start = values.start_date;
			const end = values.end_date;
			if (end.isBefore(start, "day")) {
				message.error(
					"종료일은 시작일 이후여야 합니다.",
				);
				return;
			}
			const formData = {
				leave_type: leaveType,
				reason: values.reason,
				start_date: start.format("YYYY-MM-DD"),
				end_date: end.format("YYYY-MM-DD"),
				leave_date: start.format("YYYY-MM-DD"),
			};
			const response = await useAxios.post(
				"/utils/leave/apply/",
				formData,
			);
			if (response.status === 201) {
				message.success(`${title}이 완료되었습니다.`);
				form.resetFields([
					"reason",
					"start_date",
					"end_date",
				]);
				if (userProfile) {
					form.setFieldsValue({
						name: userProfile.name,
						position:
							userProfile.classification_name ===
							"교역자"
								? "교역자"
								: "교직원",
						application_date: dayjs(),
					});
				}
			}
		} catch (error) {
			const data = error.response?.data;
			const msg =
				data?.error ||
				data?.end_date?.[0] ||
				(data && Object.values(data).flat()?.[0]) ||
				"신청 처리 중 오류가 발생했습니다.";
			message.error(String(msg));
		} finally {
			setLoading(false);
		}
	};

	const disabledDate = (current) => current && current.day() === 0;

	return (
		<ConfigProvider locale={locale}>
			<PageTitle title={title} />
			<Layout className="member-layout">
				<LeaveNavigation />
				<Layout className="member-content-layout">
					<Content className="member-content">
						<div className="member-content__inner">
							<div className="member-content__form-container page">
								<PageHeader title={title} />
								<Card className="surface-card">
									{!isSick ? (
										<>
											<Alert
												type="info"
												showIcon
												style={{
													marginBottom: 16,
												}}
												message="경조휴가는 신청 1건당 최대 5일(일요일 제외)까지 사용할 수 있습니다."
											/>
											<Alert
												type="info"
												showIcon
												style={{
													marginBottom: 16,
												}}
												message="경조휴가 증빙 서류는, 사무국에 사전 문의 후 제출해주시기 바랍니다."
											/>
										</>
									) : (
										<>
											<Alert
												type="info"
												showIcon
												style={{
													marginBottom: 16,
												}}
												message="증빙서류"
												description="병가 신청 시, 의사 소견이 포함된 진단서 등 관련 증빙서류를 사무국에 제출해야 합니다. 단, 긴급한 경우 사무국 담당자에게 사전 연락 후 증빙서류를 사후 제출할 수 있습니다."
											/>
											<Alert
												type="info"
												showIcon
												style={{
													marginBottom: 16,
												}}
												message="생활비 지급"
												description="병가 사용 일수에 따라 생활비 지급에 영향이 있습니다. 자세한 사항은 사무국에 문의해 주시기 바랍니다."
											/>
										</>
									)}
									<Form
										form={
											form
										}
										layout="vertical"
										onFinish={
											onFinish
										}>
										<Form.Item
											name="name"
											label="이름">
											<Input
												disabled
											/>
										</Form.Item>
										<Form.Item
											name="position"
											label="직분">
											<Select
												disabled>
												<Option value="교역자">
													교역자
												</Option>
												<Option value="교직원">
													교직원
												</Option>
											</Select>
										</Form.Item>
										<Form.Item
											name="application_date"
											label="신청일">
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabled
												format="YYYY-MM-DD"
											/>
										</Form.Item>
										<Form.Item
											name="reason"
											label="휴가 사유"
											rules={[
												{
													required: true,
													message: "휴가 사유를 입력해주세요",
												},
											]}>
											<TextArea
												rows={
													4
												}
											/>
										</Form.Item>
										<Form.Item
											name="start_date"
											label="시작일"
											rules={[
												{
													required: true,
													message: "시작일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabledDate={
													disabledDate
												}
												format="YYYY-MM-DD"
											/>
										</Form.Item>
										<Form.Item
											name="end_date"
											label="종료일"
											rules={[
												{
													required: true,
													message: "종료일을 선택해주세요",
												},
											]}>
											<DatePicker
												inputReadOnly={isMobile}
												style={{
													width: "100%",
												}}
												disabledDate={
													disabledDate
												}
												format="YYYY-MM-DD"
											/>
										</Form.Item>
										<Form.Item>
											<Button
												type="primary"
												htmlType="submit"
												loading={
													loading
												}
												block>
												신청하기
											</Button>
										</Form.Item>
									</Form>
								</Card>
							</div>
						</div>
					</Content>
				</Layout>
			</Layout>
		</ConfigProvider>
	);
};

export default ApplyForSpecialLeave;
